import { useEffect, useState, useMemo } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fdateShort } from "@/lib/format";
import { PageHeader, Loader, EmptyState } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ListChecks, Plus, Trash2, Clock } from "lucide-react";

const STATUS = { todo: "À faire", in_progress: "En cours", waiting: "En attente", completed: "Terminé" };

export default function Tasks() {
  const { wedding } = useAuth();
  const wid = wedding?.wedding?.id;
  const [tasks, setTasks] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", category: "Divers", priority: "medium", due_date: "" });
  const [tab, setTab] = useState("all");

  const load = async () => { const { data } = await api.get(`/weddings/${wid}/tasks`); setTasks(data); };
  useEffect(() => { if (wid) load(); }, [wid]);

  const toggle = async (t) => {
    await api.patch(`/weddings/${wid}/tasks/${t.id}`, { status: t.status === "completed" ? "todo" : "completed" });
    load();
  };
  const del = async (id) => { await api.delete(`/weddings/${wid}/tasks/${id}`); load(); };
  const save = async () => {
    try { await api.post(`/weddings/${wid}/tasks`, form); toast.success("Tâche ajoutée");
      setOpen(false); setForm({ title: "", category: "Divers", priority: "medium", due_date: "" }); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const filtered = useMemo(() => {
    if (!tasks) return [];
    if (tab === "todo") return tasks.filter((t) => t.status !== "completed");
    if (tab === "done") return tasks.filter((t) => t.status === "completed");
    if (tab === "overdue") return tasks.filter((t) => t.status !== "completed" && t.due_date && t.due_date < new Date().toISOString().slice(0, 10));
    return tasks;
  }, [tasks, tab]);

  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach((t) => { (g[t.category] = g[t.category] || []).push(t); });
    return g;
  }, [filtered]);

  if (!tasks) return <Loader />;
  const done = tasks.filter((t) => t.status === "completed").length;

  return (
    <div>
      <PageHeader eyebrow="Organisation" title="Checklist"
        description={`${done}/${tasks.length} tâches terminées`}
        actions={<Button onClick={() => setOpen(true)} data-testid="add-task-btn" className="rounded-full"><Plus className="w-4 h-4 mr-1" /> Nouvelle tâche</Button>} />

      <div className="flex gap-2 mb-6 flex-wrap">
        {[["all", "Toutes"], ["todo", "À faire"], ["overdue", "En retard"], ["done", "Terminées"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-3 py-1.5 rounded-full text-sm border ${tab === k ? "bg-stone-900 text-white" : "bg-white text-stone-600"}`}>{l}</button>
        ))}
      </div>

      {filtered.length === 0 ? <EmptyState icon={ListChecks} title="Aucune tâche" description="Votre checklist se remplit automatiquement selon votre date de mariage." /> : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([cat, items]) => (
            <div key={cat}>
              <div className="eyebrow mb-3">{cat}</div>
              <div className="card-lux divide-y">
                {items.map((t) => {
                  const overdue = t.status !== "completed" && t.due_date && t.due_date < new Date().toISOString().slice(0, 10);
                  return (
                    <div key={t.id} data-testid={`task-${t.id}`} className="flex items-center gap-3 px-5 py-3">
                      <Checkbox checked={t.status === "completed"} onCheckedChange={() => toggle(t)} data-testid={`task-check-${t.id}`} />
                      <div className="flex-1">
                        <div className={`text-sm ${t.status === "completed" ? "line-through text-stone-400" : "text-stone-800"}`}>{t.title}</div>
                        {t.due_date && <div className={`text-xs flex items-center gap-1 ${overdue ? "text-destructive" : "text-stone-400"}`}>
                          <Clock className="w-3 h-3" /> {fdateShort(t.due_date)}</div>}
                      </div>
                      <button onClick={() => del(t.id)} className="text-stone-300 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent><DialogHeader><DialogTitle>Nouvelle tâche</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Titre</Label><Input data-testid="task-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 h-10" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Catégorie</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1 h-10" /></div>
              <div><Label>Échéance</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="mt-1 h-10" /></div>
            </div>
          </div>
          <DialogFooter><Button data-testid="save-task" onClick={save} className="rounded-full">Ajouter</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
